import {
  deleteObject,
  listAll,
  ref,
  StorageReference,
  uploadBytes,
} from 'firebase/storage';
import type { ArtifactFile } from '@shared/types';
import { storage } from '@/lib/firebase';
import {
  contentTypeForFormat,
  getArtifactFilePath,
} from '@/lib/firebaseAssetPaths';
import type { OpenSCADExportFormat } from '@/worker/types';

export async function deleteStorageFolderRecursively(
  folderRef: StorageReference,
): Promise<void> {
  const listing = await listAll(folderRef);

  await Promise.all(listing.items.map((item) => deleteObject(item)));
  await Promise.all(
    listing.prefixes.map((prefix) => deleteStorageFolderRecursively(prefix)),
  );
}

const ROLE_FOR_FORMAT: Record<ArtifactFile['format'], ArtifactFile['role']> = {
  stl: 'preview',
  '3mf': 'manufacturing',
  scad: 'source',
  svg: 'drawing',
  pdf: 'drawing',
  gltf: 'preview',
  glb: 'preview',
};

/**
 * Upload all bytes produced by a multi-export compile, plus the SCAD source,
 * and return the corresponding `ArtifactFile[]` ready to attach to a
 * ParametricArtifact. STL is treated as the preview-role file; 3MF as the
 * manufacturing-role file (when the worker actually produced bytes); SCAD is
 * always uploaded as a source-role file.
 */
export async function uploadArtifactFiles(
  userId: string,
  conversationId: string,
  messageId: string,
  outputs: Partial<Record<OpenSCADExportFormat, Uint8Array>>,
  scadSource: string,
): Promise<ArtifactFile[]> {
  const files: ArtifactFile[] = [];
  const now = new Date().toISOString();

  for (const [formatKey, bytes] of Object.entries(outputs)) {
    if (!bytes) continue;
    const format = formatKey as ArtifactFile['format'];
    const path = getArtifactFilePath(
      userId,
      conversationId,
      messageId,
      format,
    );
    const contentType = contentTypeForFormat(format);
    await uploadBytes(ref(storage, path), bytes as Uint8Array, { contentType });
    files.push({
      format,
      storagePath: path,
      contentType,
      role: ROLE_FOR_FORMAT[format] ?? 'preview',
      byteLength: (bytes as Uint8Array).byteLength,
      generatedAt: now,
    });
  }

  if (scadSource) {
    const path = getArtifactFilePath(userId, conversationId, messageId, 'scad');
    const contentType = contentTypeForFormat('scad');
    const sourceBytes = new TextEncoder().encode(scadSource);
    await uploadBytes(ref(storage, path), sourceBytes, { contentType });
    files.push({
      format: 'scad',
      storagePath: path,
      contentType,
      role: 'source',
      byteLength: sourceBytes.byteLength,
      generatedAt: now,
    });
  }

  return files;
}
